import { formatUnknownRecordValueForDisplay } from "#src/utils/formatUnknownRecordValueForDisplay";
import {
  KeyValuePairList,
  KeyValuePanel,
} from "@validereinc/common-components";
import {
  InputOutputSchemaPropertyConfigurationType,
  WorkerRunStatus,
  WorkerRunType,
  WorkerVersionType,
} from "@validereinc/domain";
import React, { useMemo } from "react";

/**
 * Returns a function that maps schema property keys to `display_name` when defined, else the key.
 */
const buildWorkerRunIoDisplayTitleResolver = (
  schema:
    | Record<string, InputOutputSchemaPropertyConfigurationType>
    | undefined
    | null
): ((propertyKey: string) => string) => {
  const map = new Map<string, string>();
  if (schema != null && typeof schema === "object") {
    for (const [key, config] of Object.entries(schema)) {
      const displayName = config?.display_name?.trim();
      map.set(
        key,
        displayName !== "" && displayName != null ? displayName : key
      );
    }
  }
  return (propertyKey: string) => map.get(propertyKey) ?? propertyKey;
};

/**
 * Formats worker run field values for display in a key-value list (same rules as
 * {@link formatUnknownRecordValueForDisplay}); empty values show an em dash.
 */
const formatWorkerRunIoValue = (value: unknown): React.ReactNode => {
  const formatted = formatUnknownRecordValueForDisplay(value, {
    shouldHideFileIcon: true,
  });
  return formatted === "" ? "—" : formatted;
};

/**
 * Renders a worker run `inputs` / `outputs` map as key-value rows.
 */
const workerRunIoRecordToKeyValueData = (
  record: Record<string, unknown>,
  resolveRowTitle: (propertyKey: string) => string = (k) => k
): KeyValuePairList => {
  const entries = Object.entries(record ?? {}).sort(([a], [b]) =>
    resolveRowTitle(a).localeCompare(resolveRowTitle(b), undefined, {
      sensitivity: "base",
    })
  );
  if (entries.length === 0) {
    return [{ title: "Values", value: "—" }];
  }
  return entries.map(([key, value]) => ({
    title: resolveRowTitle(key),
    value: formatWorkerRunIoValue(value),
  }));
};

const WorkerInputOutputDetails = ({
  type,
  workerRun,
  workerVersion,
}: {
  type: "input" | "output";
  workerRun: WorkerRunType;
  workerVersion?: WorkerVersionType;
}) => {
  const resolveInputRowTitle = useMemo(
    () => buildWorkerRunIoDisplayTitleResolver(workerVersion?.input_schema),
    [workerVersion?.input_schema]
  );
  const resolveOutputRowTitle = useMemo(
    () => buildWorkerRunIoDisplayTitleResolver(workerVersion?.output_schema),
    [workerVersion?.output_schema]
  );

  const inputsKeyValueData = useMemo(
    () =>
      workerRun != null
        ? workerRunIoRecordToKeyValueData(
            workerRun.inputs as Record<string, unknown>,
            resolveInputRowTitle
          )
        : [],
    [workerRun?.inputs, resolveInputRowTitle]
  );

  const outputsKeyValueData = useMemo((): KeyValuePairList => {
    if (workerRun == null) return [];
    if (workerRun.status === WorkerRunStatus.STARTED) {
      return [
        {
          title: "—",
          value: (
            <span style={{ color: "var(--color-text-secondary)" }}>
              Not available yet
            </span>
          ),
        },
      ];
    }
    return workerRunIoRecordToKeyValueData(
      workerRun.outputs as Record<string, unknown>,
      resolveOutputRowTitle
    );
  }, [workerRun?.outputs, workerRun?.status, resolveOutputRowTitle]);

  return (
    <KeyValuePanel
      data={type === "input" ? inputsKeyValueData : outputsKeyValueData}
      panelKeyValueListProps={{ variant: "shaded", color: "neutral" }}
      panelProps={{
        title: type === "input" ? "Inputs" : "Outputs",
        isFluidY: true,
        loaded: true,
      }}
    />
  );
};

export default WorkerInputOutputDetails;
